import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import useAbortControllers from '@/hooks/AbortControllers'
import {
  getAnalyticsRepairOrders,
  getAnalyticsRepairOrdersPage,
} from '@/services/analytics.service'
import { formatCurrency } from '@/utils/formatters'

// Page size for the drill-down list; "Load more" pulls the next page via the
// response's `next` link.
const PAGE_LIMIT = 100

/**
 * RoDrilldownSlideout — read-only right-side panel listing the repair orders
 * behind a clicked Detailed Data row. `query` is the RO-list filter string
 * (date range on the metric's timestamp + advisor/technician filters).
 */
const RoDrilldownSlideout = ({ open, onClose, query, title, subtitle }) => {
  const { getSignal } = useAbortControllers()
  const [repairOrders, setRepairOrders] = useState([])
  const [count, setCount] = useState(0)
  const [nextUrl, setNextUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || !query) return
    // Drop the previous query's results before fetching so reopening (or a new
    // bucket) never flashes stale repair orders.
    setError(null)
    setRepairOrders([])
    setCount(0)
    setNextUrl(null)
    const load = async () => {
      const response = await getAnalyticsRepairOrders(
        `limit=${PAGE_LIMIT}&order_by=-created_at&${query}`,
        setLoading,
        setError,
        getSignal('analytics-drilldown'),
      )
      if (response) {
        setRepairOrders(response.results)
        setCount(response.count)
        setNextUrl(response.next)
      }
    }
    load()
  }, [open, query])

  const loadMore = async () => {
    if (!nextUrl) return
    const response = await getAnalyticsRepairOrdersPage(
      nextUrl,
      setLoadingMore,
      setError,
      getSignal('analytics-drilldown-more'),
    )
    if (response) {
      setRepairOrders((prev) => [...prev, ...response.results])
      setNextUrl(response.next)
    }
  }

  const renderList = () => {
    if (loading) return <p className="text-gray text-sm">Loading…</p>
    if (repairOrders.length === 0)
      return <p className="text-gray text-sm">No repair orders match.</p>
    return (
      <>
        <p className="text-charcoal-400 mb-3 text-xs">
          Showing {repairOrders.length} of {count} repair order{count === 1 ? '' : 's'}
        </p>
        <ul className="divide-gray-light divide-y">
          {repairOrders.map((ro) => (
            <li key={ro.id}>
              <Link
                to={`/dealer/${ro.dealer?.id}/repair-order/${ro.id}`}
                onClick={onClose}
                className="hover:bg-background -mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-3"
              >
                <div className="min-w-0">
                  <p className="text-charcoal-900 truncate text-sm font-semibold">{ro.roNumber}</p>
                  <p className="text-gray truncate text-xs">
                    {ro.dealer?.name}
                    {ro.advisor ? ` · ${ro.advisor}` : ''}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-charcoal-900 text-sm font-medium">
                    {formatCurrency(ro.totalClaimAmount)}
                  </p>
                  <p className="text-gray text-xs">{ro.state || ro.status}</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
        {nextUrl && (
          <div className="mt-4 flex justify-center">
            <Button
              label={loadingMore ? 'Loading…' : 'Load more'}
              onClick={loadMore}
              loading={loadingMore}
              background="bg-white"
              size="sm"
              outlined
            />
          </div>
        )}
      </>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="data-closed:opacity-0 fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out"
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <DialogPanel
              transition
              className="data-closed:translate-x-full pointer-events-auto w-screen max-w-lg transition duration-500 ease-in-out sm:duration-700"
            >
              <div className="flex h-full flex-col bg-white shadow-xl">
                <div className="border-gray-light flex items-start justify-between border-b p-4 sm:px-6">
                  <div className="min-w-0">
                    <DialogTitle className="text-charcoal-900 text-lg font-semibold">
                      {title}
                    </DialogTitle>
                    {subtitle && <p className="text-gray truncate text-sm">{subtitle}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-charcoal-400 hover:text-charcoal-900 ml-3 shrink-0"
                  >
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                  </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:px-6">
                  {error && <p className="text-red mb-2 text-sm font-semibold">{error}</p>}
                  {renderList()}
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

RoDrilldownSlideout.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  query: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
}

export default RoDrilldownSlideout
